import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with credentials for httpOnly cookies
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Enable sending cookies with cross-origin requests
});

// Request interceptor - no need to add token header, httpOnly cookie handles auth
api.interceptors.request.use((config) => {
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only redirect on 401 if:
    // 1. Not already on login/register/reset-password pages
    // 2. Not a probe request like /auth/me (used by AuthContext.initAuth)
    const isAuthProbe = error.config?.url?.endsWith('/auth/me');
    const isPublicRoute = window.location.pathname.startsWith('/login') || 
                          window.location.pathname.startsWith('/register') ||
                          window.location.pathname.startsWith('/reset-password') ||
                          window.location.pathname.startsWith('/forgot-password');
    
    if (error.response?.status === 401 && !isPublicRoute && !isAuthProbe) {
      // Clean up legacy localStorage
      localStorage.removeItem('hrms_token');
      localStorage.removeItem('hrms_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  getCaptcha: (type = 'text') => api.get(`/auth/captcha?captcha_type=${type}`),
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  logout: () => api.post('/auth/logout'),
};

// User Management APIs
export const userAPI = {
  getAll: () => api.get('/users'),
  updateRole: (userId, role) => api.put(`/users/${userId}/role?role=${role}`),
};

// Department APIs
export const departmentAPI = {
  getAll: () => api.get('/departments'),
  create: (data) => api.post('/departments', data),
};

// Employee APIs
export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

// Salary APIs
export const salaryAPI = {
  getAll: (params) => api.get('/salary', { params }),
  create: (data) => api.post('/salary', data),
  update: (id, data) => api.put(`/salary/${id}`, data),
  lock: (id) => api.post(`/salary/${id}/lock`),
  workflow: (id, data) => api.post(`/salary/${id}/workflow`, data),
  bulkSubmit: (month) => api.post(`/salary/bulk-submit?month=${month}`),
  bulkDisburse: (month) => api.post(`/salary/bulk-disburse?month=${month}`),
  getPayslip: (employeeId, month) => api.get(`/salary/payslip/${employeeId}/${month}`),
  getAudit: (employeeId) => api.get(`/salary/audit/${employeeId}`),
  generateExcel: (month) => api.post(`/salary/generate-excel?month=${month}`, {}, { responseType: 'blob' }),
  // Batch APIs
  generateBatch: (month, year) => api.post(`/salary/batch/generate?month=${month}&year=${year}`),
  getBatches: (status) => api.get('/salary/batches', { params: status ? { status } : {} }),
  getBatch: (batchId) => api.get(`/salary/batch/${batchId}`),
  updateBatchRecord: (batchId, recordId, data) => api.put(`/salary/batch/${batchId}/record/${recordId}`, data),
  submitBatchForVerification: (batchId) => api.post(`/salary/batch/${batchId}/submit-for-verification`),
  authorizeBatch: (batchId) => api.post(`/salary/batch/${batchId}/authorize`),
  rejectBatch: (batchId, reason) => api.post(`/salary/batch/${batchId}/reject?reason=${encodeURIComponent(reason || '')}`),
  deleteBatch: (batchId) => api.delete(`/salary/batch/${batchId}`),
  downloadBatchExcel: (batchId) => api.get(`/salary/batch/${batchId}/download`, { responseType: 'blob' }),
  getBatchPayslip: (batchId, recordId) => api.get(`/salary/batch/${batchId}/payslip/${recordId}`),
  downloadPayslipPdf: (batchId, recordId) => api.get(`/salary/batch/${batchId}/payslip/${recordId}/pdf`, { responseType: 'blob' }),
  getMyPayslips: () => api.get('/salary/my-payslips'),
};

// Advanced Payroll Engine APIs
export const payrollEngineAPI = {
  // Pay Components
  getComponents: (type) => api.get('/payroll-engine/components', { params: type ? { type } : {} }),
  createComponent: (data) => api.post('/payroll-engine/components', data),
  updateComponent: (id, data) => api.put(`/payroll-engine/components/${id}`, data),
  
  // Pay Structures
  getStructures: () => api.get('/payroll-engine/structures'),
  createStructure: (data) => api.post('/payroll-engine/structures', data),
  
  // Statutory Rates
  getStatutoryRates: () => api.get('/payroll-engine/statutory-rates'),
  createStatutoryRate: (data) => api.post('/payroll-engine/statutory-rates', data),
  
  // Tax Slabs
  getTaxSlabs: (params) => api.get('/payroll-engine/tax-slabs', { params }),
  createTaxSlab: (data) => api.post('/payroll-engine/tax-slabs', data),
  
  // Payroll Runs
  createRun: (name, month, year, departmentId) => 
    api.post(`/payroll-engine/runs?name=${encodeURIComponent(name)}&month=${month}&year=${year}${departmentId ? `&department_id=${departmentId}` : ''}`),
  getRuns: (params) => api.get('/payroll-engine/runs', { params }),
  getRun: (runId) => api.get(`/payroll-engine/runs/${runId}`),
  calculatePayroll: (runId) => api.post(`/payroll-engine/runs/${runId}/calculate`),
  verifyPayroll: (runId) => api.post(`/payroll-engine/runs/${runId}/verify`),
  approvePayroll: (runId) => api.post(`/payroll-engine/runs/${runId}/approve`),
  
  // Salary Slips
  getSlips: (runId, params) => api.get(`/payroll-engine/runs/${runId}/slips`, { params }),
  getSlip: (slipId) => api.get(`/payroll-engine/slips/${slipId}`),
  getMySlips: (year) => api.get('/payroll-engine/slips/my', { params: year ? { year } : {} }),
  downloadSlipPdf: (slipId) => api.get(`/payroll-engine/slips/${slipId}/pdf`, { responseType: 'blob' }),
  downloadSlipByCode: (runId, employeeCode) => api.get(`/payroll-engine/runs/${runId}/slips/${employeeCode}/pdf`, { responseType: 'blob' }),
  
  // Bank File
  generateBankFile: (runId, format) => api.post(`/payroll-engine/runs/${runId}/generate-bank-file?format=${format}`),
  
  // Arrears
  createArrears: (data) => api.post('/payroll-engine/arrears', data),
  getArrears: (params) => api.get('/payroll-engine/arrears', { params }),
  
  // Reports
  getSummary: (month, year, departmentId) => 
    api.get(`/payroll-engine/reports/summary?month=${month}&year=${year}${departmentId ? `&department_id=${departmentId}` : ''}`),
  getDepartmentWise: (month, year) => api.get(`/payroll-engine/reports/department-wise?month=${month}&year=${year}`),
};

// Leave APIs
export const leaveAPI = {
  apply: (data) => api.post('/leave/apply', data),
  getAll: (params) => api.get('/leave', { params }),
  processAction: (id, data) => api.put(`/leave/${id}/action`, data),
  cancel: (id) => api.put(`/leave/${id}/cancel`),
  getBalance: (employeeId) => api.get(`/leave/balance/${employeeId}`),
  getLeaveBalances: () => api.get('/leave/my-balances'),
  encashment: (data) => api.post('/leave/encashment', data),
};

// Dashboard APIs
export const dashboardAPI = {
  getAdmin: () => api.get('/dashboard/admin'),
  getVerifier: () => api.get('/dashboard/verifier'),
  getEmployee: () => api.get('/dashboard/employee'),
};

// Reports APIs
export const reportAPI = {
  getSalaryRegister: (month) => api.get(`/reports/salary-register?month=${month}`),
  getLeaveUtilization: (year) => api.get(`/reports/leave-utilization?year=${year}`),
  getEmployeeSalaryHistory: (employeeId) => api.get(`/reports/employee-salary-history/${employeeId}`),
  getAttendanceSummary: (month, departmentId) => api.get(`/reports/attendance-summary?month=${month}${departmentId ? `&department_id=${departmentId}` : ''}`),
  getPayrollSummary: (month) => api.get(`/reports/payroll-summary?month=${month}`),
  getClaimsAnalytics: (startDate, endDate) => api.get(`/reports/claims-analytics${startDate ? `?start_date=${startDate}&end_date=${endDate}` : ''}`),
  getHRAnalytics: () => api.get('/reports/hr-analytics'),
};

// Shift APIs
export const shiftAPI = {
  getAll: () => api.get('/shifts'),
  create: (data) => api.post('/shifts', data),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  assign: (employeeId, shiftId, startDate, endDate) => 
    api.post(`/shifts/assign?employee_id=${employeeId}&shift_id=${shiftId}&start_date=${startDate}${endDate ? `&end_date=${endDate}` : ''}`),
  getEmployeeShift: (employeeId) => api.get(`/shifts/assignments/${employeeId}`),
};

// Attendance APIs
export const attendanceAPI = {
  checkIn: (data) => api.post('/attendance/check-in', data),
  checkOut: (data) => api.post('/attendance/check-out', data),
  checkInWithFace: (data) => api.post('/attendance/check-in-with-face', data),
  checkOutWithFace: (data) => api.post('/attendance/check-out-with-face', data),
  getMyAttendance: (month) => api.get(`/attendance/my-attendance${month ? `?month=${month}` : ''}`),
  getToday: () => api.get('/attendance/today'),
  getAll: (params) => api.get('/attendance/all', { params }),
  regularize: (employeeId, data) => api.post(`/attendance/regularize?employee_id=${employeeId}`, data),
  getSummary: (employeeId, month) => api.get(`/attendance/summary/${employeeId}?month=${month}`),
  // New: Employee-specific attendance
  getEmployeeHistory: (employeeId, days = 7) => api.get(`/attendance/employee/${employeeId}/history?days=${days}`),
  getEmployeeYearly: (employeeId, year) => api.get(`/attendance/employee/${employeeId}/yearly${year ? `?year=${year}` : ''}`),
  // Admin: Check-in/out for other employees
  checkInForEmployee: (employeeId, data) => api.post(`/attendance/check-in-for/${employeeId}`, data),
  checkOutForEmployee: (employeeId, data) => api.post(`/attendance/check-out-for/${employeeId}`, data),
  // Admin: Update attendance
  updateAttendance: (attendanceId, data) => api.put(`/attendance/${attendanceId}`, data),
};

// Leave Enhancement APIs
export const leaveEnhancementAPI = {
  // Escalation
  getEscalationRules: () => api.get('/escalation-rules'),
  createEscalationRule: (params) => api.post('/escalation-rules', null, { params }),
  checkEscalations: () => api.post('/leaves/check-escalations'),
  getEscalatedLeaves: () => api.get('/leaves/escalated'),
  
  // Comp-Off
  createCompOffRequest: (data) => api.post('/comp-off/request', data),
  requestCompOff: (data) => api.post('/comp-off/request', data),
  getMyCompOffRequests: () => api.get('/comp-off/my-requests'),
  getMyCompOffBalance: () => api.get('/comp-off/my-balance'),
  getAllCompOffRequests: (status) => api.get(`/comp-off/all${status ? `?status=${status}` : ''}`),
  processCompOff: (id, payload) => 
    api.put(`/comp-off/${id}/action`, payload),
  
  // Encashment
  createEncashmentRequest: (data) => api.post('/leave-encashment/request', data),
  requestEncashment: (data) => api.post('/leave-encashment/request', data),
  getMyEncashmentRequests: () => api.get('/leave-encashment/my-requests'),
  getAllEncashmentRequests: (status) => api.get(`/leave-encashment/all${status ? `?status=${status}` : ''}`),
  processEncashment: (id, payload) => 
    api.put(`/leave-encashment/${id}/action`, payload),
};

// Geo-Fence APIs
export const geoFenceAPI = {
  create: (data) => api.post('/geo-fences', data),
  getAll: () => api.get('/geo-fences'),
  delete: (id) => api.delete(`/geo-fences/${id}`),
  verifyLocation: (lat, lng) => api.post(`/geo-fences/verify-location?latitude=${lat}&longitude=${lng}`),
  assignEmployees: (fenceId, employeeIds) => api.post(`/geo-fences/assign-employees/${fenceId}`, employeeIds),
  unassignEmployees: (fenceId, employeeIds) => api.delete(`/geo-fences/unassign-employees/${fenceId}`, { data: employeeIds }),
  getOfficeEmployees: (fenceId) => api.get(`/geo-fences/${fenceId}/employees`),
};

// Face Recognition APIs
export const faceAPI = {
  register: (data) => api.post('/face/register', data),
  getMyRegistration: () => api.get('/face/my-registration'),
};

// Tax & Declarations APIs
export const taxAPI = {
  getSettings: () => api.get('/tax-settings'),
  saveSettings: (data) => api.post('/tax-settings', data),
  getMyDeclarations: (financialYear) => api.get(`/investment-declarations/my${financialYear ? `?financial_year=${financialYear}` : ''}`),
  getAllDeclarations: (financialYear, status) => 
    api.get(`/investment-declarations/all?financial_year=${financialYear}${status ? `&status=${status}` : ''}`),
  createDeclaration: (data) => api.post('/investment-declarations', data),
  submitDeclaration: (id) => api.put(`/investment-declarations/${id}/submit`),
  verifyDeclaration: (id) => api.put(`/investment-declarations/${id}/verify`),
  acceptDeclaration: (id) => api.put(`/investment-declarations/${id}/accept`),
  rejectDeclaration: (id, reason) => api.put(`/investment-declarations/${id}/reject?reason=${encodeURIComponent(reason || '')}`),
  getStats: (financialYear) => api.get(`/investment-declarations/stats?financial_year=${financialYear}`),
  getAuditTrail: (id) => api.get(`/investment-declarations/${id}/audit-trail`),
  calculateTax: (employeeId, financialYear) => api.get(`/tax-calculator/${employeeId}?financial_year=${financialYear}`),
  // Sub-head limits configuration (admin)
  getSubheadLimits: () => api.get('/subhead-limits'),
  updateSubheadLimits: (limits) => api.put('/subhead-limits', { limits }),
  getDefaultSubheadLimits: () => api.get('/subhead-limits/defaults'),
};

// Claims APIs
export const claimsAPI = {
  create: (data) => api.post('/claims', data),
  createOnBehalf: (employeeId, data) => api.post('/claims/on-behalf', { ...data, employee_id: employeeId }),
  getMyClaims: (status) => api.get(`/claims/my${status ? `?status=${status}` : ''}`),
  getAll: (status, category) => api.get(`/claims/all${status || category ? '?' : ''}${status ? `status=${status}` : ''}${status && category ? '&' : ''}${category ? `category=${category}` : ''}`),
  processAction: (id, action, data = {}) => api.put(`/claims/${id}/action`, { action, ...data }),
  getSummary: (employeeId) => api.get(`/claims/summary${employeeId ? `?employee_id=${employeeId}` : ''}`),
};

// Settings APIs
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  get: (key) => api.get(`/settings/${key}`),
  update: (key, value) => api.put(`/settings/${key}`, { value }),
};

// Audit APIs
export const auditAPI = {
  getLogs: (params) => api.get('/audit-logs', { params }),
};

// Ticket/Help Desk APIs
export const ticketAPI = {
  create: (data) => api.post('/tickets', data),
  getMyTickets: (status) => api.get(`/tickets/my${status ? `?status=${status}` : ''}`),
  getAll: (status, category, priority) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    if (priority) params.append('priority', priority);
    return api.get(`/tickets/all${params.toString() ? `?${params.toString()}` : ''}`);
  },
  getById: (id) => api.get(`/tickets/${id}`),
  updateStatus: (id, status, resolution) => 
    api.put(`/tickets/${id}/status?status=${status}${resolution ? `&resolution=${encodeURIComponent(resolution)}` : ''}`),
  assign: (id, assigneeId) => api.put(`/tickets/${id}/assign?assignee_id=${assigneeId}`),
  addComment: (id, data) => api.post(`/tickets/${id}/comments`, data),
  getStats: () => api.get('/tickets/stats'),
};

// Registration APIs
export const registrationAPI = {
  register: (data) => api.post('/registration/register', data),
  getPending: (skip = 0, limit = 50) => api.get(`/registration/registrations/pending?skip=${skip}&limit=${limit}`),
  getAll: (status, skip = 0, limit = 50) => api.get(`/registration/registrations/all?skip=${skip}&limit=${limit}${status ? `&status=${status}` : ''}`),
  getStats: () => api.get('/registration/registrations/stats'),
  processAction: (requestId, data) => api.put(`/registration/registrations/${requestId}/action`, data),
};

// Bulk Upload APIs
export const bulkUploadAPI = {
  uploadEmployees: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Remove Content-Type header to let browser set it with boundary for multipart/form-data
    return api.post('/bulk-upload/employees', formData, {
      headers: { 'Content-Type': undefined }
    });
  },
  downloadTemplate: () => api.get('/bulk-upload/template/employees', { responseType: 'blob' }),
  getHistory: (skip = 0, limit = 20) => api.get(`/bulk-upload/history?skip=${skip}&limit=${limit}`),
};

// File Upload APIs
export const fileUploadAPI = {
  uploadClaimReceipt: (claimId, fileOrFormData) => {
    // Accept either a File object or FormData
    let formData;
    if (fileOrFormData instanceof FormData) {
      formData = fileOrFormData;
    } else {
      formData = new FormData();
      formData.append('file', fileOrFormData);
    }
    // Remove Content-Type header to let browser set it with boundary for multipart/form-data
    return api.post(`/files/claim-receipt/${claimId}`, formData, {
      headers: { 'Content-Type': undefined }
    });
  },
  uploadClaimAttachment: (formData) => {
    // Upload attachment before claim is created
    return api.post('/files/claim-attachment', formData, {
      headers: { 'Content-Type': undefined }
    });
  },
  deleteClaimReceipt: (claimId) => api.delete(`/files/claim-receipt/${claimId}`),
};

// Push Notification APIs
export const notificationAPI = {
  subscribe: (subscription) => api.post('/notifications/subscribe', subscription),
  unsubscribe: () => api.delete('/notifications/unsubscribe'),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (data) => api.put('/notifications/preferences', data),
};

// Seed Data
export const seedData = () => api.post('/seed-data');

// Employee Master (360°) APIs
export const employeeMasterAPI = {
  // Core CRUD
  getAll: (params) => api.get('/employee-master', { params }),
  getById: (id) => api.get(`/employee-master/${id}`),
  getByCode: (code) => api.get(`/employee-master/code/${code}`),
  create: (data) => api.post('/employee-master', data),
  update: (id, data) => api.put(`/employee-master/${id}`, data),
  authorize: (id, action, remarks) => 
    api.post(`/employee-master/${id}/authorize?action=${action}${remarks ? `&remarks=${encodeURIComponent(remarks)}` : ''}`),
  
  // Addresses
  addAddress: (empId, data) => api.post(`/employee-master/${empId}/addresses`, data),
  updateAddress: (empId, addrId, data) => api.put(`/employee-master/${empId}/addresses/${addrId}`, data),
  deleteAddress: (empId, addrId) => api.delete(`/employee-master/${empId}/addresses/${addrId}`),
  
  // Family
  addFamily: (empId, data) => api.post(`/employee-master/${empId}/family`, data),
  updateFamily: (empId, memberId, data) => api.put(`/employee-master/${empId}/family/${memberId}`, data),
  deleteFamily: (empId, memberId) => api.delete(`/employee-master/${empId}/family/${memberId}`),
  
  // Nominees
  addNominee: (empId, data) => api.post(`/employee-master/${empId}/nominees`, data),
  updateNominee: (empId, nomineeId, data) => api.put(`/employee-master/${empId}/nominees/${nomineeId}`, data),
  
  // Education
  addEducation: (empId, data) => api.post(`/employee-master/${empId}/education`, data),
  
  // Experience
  addExperience: (empId, data) => api.post(`/employee-master/${empId}/experience`, data),
  
  // Bank Accounts
  addBankAccount: (empId, data) => api.post(`/employee-master/${empId}/bank-accounts`, data),
  
  // Assets & Liabilities
  addAsset: (empId, data) => api.post(`/employee-master/${empId}/assets`, data),
  addLiability: (empId, data) => api.post(`/employee-master/${empId}/liabilities`, data),
  
  // Posting History
  addPosting: (empId, data) => api.post(`/employee-master/${empId}/posting-history`, data),
  
  // Documents
  addDocument: (empId, data) => api.post(`/employee-master/${empId}/documents`, data),
  getDocuments: (empId, type) => api.get(`/employee-master/${empId}/documents${type ? `?document_type=${type}` : ''}`),
  
  // Search & Reports
  advancedSearch: (params) => api.get('/employee-master/search/advanced', { params }),
  getRetirementDue: (months = 6) => api.get(`/employee-master/reports/retirement-due?months=${months}`),
  getProbationDue: (days = 30) => api.get(`/employee-master/reports/probation-due?days=${days}`),
  getStats: () => api.get('/employee-master/stats/summary'),
  
  // Delete & Restore
  deleteEmployee: (empId, hardDelete = false) => api.delete(`/employee-master/${empId}?hard_delete=${hardDelete}`),
  restoreEmployee: (empId) => api.post(`/employee-master/${empId}/restore`),
  
  // Bulk Operations
  bulkDelete: (employeeIds, hardDelete = false) => api.post(`/employee-master/bulk/delete?hard_delete=${hardDelete}`, employeeIds),
  bulkRestore: (employeeIds) => api.post('/employee-master/bulk/restore', employeeIds),
  
  // Duplicates & Merge
  findDuplicates: (field = 'email') => api.get(`/employee-master/duplicates?field=${field}`),
  mergeEmployees: (primaryId, secondaryId, mergeFields = []) => 
    api.post(`/employee-master/merge?primary_id=${primaryId}&secondary_id=${secondaryId}${mergeFields.length ? `&merge_fields=${mergeFields.join(',')}` : ''}`),
};

// Import Jobs API (Async bulk import)
export const importJobsAPI = {
  startAsyncImport: (formData) => api.post('/import-jobs/import/async', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000  // 5 minutes timeout for large file uploads
  }),
  getJobStatus: (jobId) => api.get(`/import-jobs/import/jobs/${jobId}/status`),
  cancelJob: (jobId) => api.post(`/import-jobs/import/jobs/${jobId}/cancel`),
  rollbackJob: (jobId, hardDelete = false) => api.post(`/import-jobs/import/jobs/${jobId}/rollback?hard_delete=${hardDelete}`),
  getHistory: (page = 1, limit = 20) => api.get('/import-jobs/import/history', { params: { page, limit } }),
  getHistoryDetail: (historyId) => api.get(`/import-jobs/import/history/${historyId}`),
  // Templates
  getTemplates: () => api.get('/import-jobs/templates'),
  getTemplate: (templateId) => api.get(`/import-jobs/templates/${templateId}`),
  downloadTemplate: (templateId) => `${api.defaults.baseURL}/import-jobs/templates/${templateId}/download`,
  // SSE Stream URL (use with EventSource)
  getStreamUrl: (jobId) => `${api.defaults.baseURL}/import-jobs/import/jobs/${jobId}/stream`,
};

export default api;
